import {type Epic, ofType, type ActionsObservable} from 'redux-observable';
import {
    merge,
    fromEvent,
    timer,
    of,
    combineLatest,
    type Observable,
    interval,
    identity,
    Subject,
    ReplaySubject,
    EMPTY,
} from 'rxjs';
import {
    tap,
    map,
    filter,
    exhaustMap,
    switchMap,
    repeatWhen,
    take,
    ignoreElements,
    takeUntil,
    flatMap,
    throttleTime,
    retryWhen,
    distinctUntilChanged,
    takeWhile,
} from 'rxjs/operators';
import moment from 'moment-timezone';
import {type Action} from 'redux';

import {type RootState} from 'web-app/react/redux/root-state';
import * as actionTypes from 'web-app/react/constants/action-types';
import {type InferAction} from 'web-app/util/redux/typed-action-helpers';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import {genericRetryStrategy, type InferObservable} from 'web-app/util/rxjs';
import {checkIfFlagIsToggled, DebugLocalStorageFlags} from 'web-app/react/routes/debugger/local-storage-config';

import * as Actions from './actions';
import * as Selectors from '../selectors';

type Seconds = number;

export const defaultTimeLeftBeforeWarn: Seconds = 30;
export const defaultTimeBetweenExtend: Seconds = 30;
export const defaultTimeSubtractedFromExpiration: Seconds = 15;

type UnixTimestamp = number;
type ExpirationTimestampObservable = Observable<UnixTimestamp>;
type LastRefreshTimestampObservable = Observable<UnixTimestamp>;
type ExtendSessionObservable = () => Observable<Seconds>;
type PersistExpirationTimestamp = (timestamp: string) => void;

/** ******************************
 * Observables
 ********************************/

/**
 * Call this to extend the current session and save the new expiration to Local Storage
 * @param seconds: number
 * @returns `Observable$`
 */

const extendSession$ = (
    handleExtendSession: ExtendSessionObservable,
    persistExpirationTimestamp: PersistExpirationTimestamp,
) =>
    // Extend session and receive new time of access token expiration
    handleExtendSession().pipe(
        tap(secondsToExpiration => {
            if (checkIfFlagIsToggled(DebugLocalStorageFlags.SHOW_AUTO_LOGOUT_STATS.flag)) {
                debuggingSubjects.sessionLifetime$.next(secondsToExpiration);
            }
        }),
        /**
         * Subtracting another 15 seconds, so we expire and warn the user before the backend actually expires it.
         * This is an arbitrary decision, but the reasoning is that we don't want to allow the session to be extendended
         * when it's very close the actually expire. Otherwise a user might click "Stay logged in" when there's little time left
         * and then receive a "Your session has expired" from the backend as a response.
         *
         * This is greenlit by Nicolai Dahl and Christian Harrington.
         */
        map(secondsToExpiration => secondsToExpiration - defaultTimeSubtractedFromExpiration),
        tap(secondsToExpiration => {
            // Here we calculate the new expiration of the access token
            const expirationTimestamp = moment()
                // Add the time which the session should be extended to the time the reqest was fired
                .add(secondsToExpiration, 'seconds')

                .format();
            // Save time of expiry in local storage
            persistExpirationTimestamp(expirationTimestamp);
        }),
        map(expirationTimestamp => Actions.extendSessionSuccess.action(expirationTimestamp)),
        retryWhen(genericRetryStrategy({maxRetryAttempts: 5})),
    );

// Cross browser prefixes 🙄
const getFullScreenElement = (): Element | null =>
    document.fullscreenElement ||
    (document as any).mozFullScreenElement ||
    (document as any).webkitFullscreenElement ||
    (document as any).msFullscreenElement;

/**
 * Observable$ that should emit whenever something takes the full screen, e.g. a <video/> element
 */
const fullScreenChange$ = merge(
    fromEvent(document, 'fullscreenchange'),
    fromEvent(document, 'mozonfullscreenchange'),
    fromEvent(document, 'MSFullscreenChange'),
    fromEvent(document, 'webkitfullscreenchange'),
);

const isVideoElement = (element: Element): element is HTMLVideoElement => element.tagName === 'VIDEO';

/**
 * Observable$ that continually polls and emits if one of the filters in the inner `merge` passes.
 * Useful for extending session when user is considered "active" without interacting with their device.
 */
export const userActivityPolling$ = merge(interval(5000), fullScreenChange$).pipe(
    // Emit when full screen element is video that is playing.
    flatMap(() =>
        of(getFullScreenElement()).pipe(
            filter(hasValue),
            filter(isVideoElement),
            filter(element => !element.paused),
        ),
    ),
);

/**
 * `Observable$` that emits when we detect activity from the user.
 */
export const userEvent$ = merge(
    // https://developer.mozilla.org/en-US/docs/Web/API/Element/click_event
    // Fires when anything is clicked
    fromEvent(document, 'click'),
    // https://developer.mozilla.org/en-US/docs/Web/API/Document/keydown_event
    // Fires when any key is pressed
    fromEvent(document, 'keydown'),
    // https://developer.mozilla.org/en-US/docs/Web/API/Document/scroll_event
    /**
     * Fires when any element scrolls.
     * - `capture` is what allows us to catch scroll events on any elements
     * - `throttleTime` limits this to emit at most every 500ms. Scroll events are emitted frequently and could (?) be resource heavy.
     */
    fromEvent(document, 'scroll', {capture: true}).pipe(throttleTime(500)),
);

export const userActivity$ = merge(userEvent$, userActivityPolling$);

/**
 * `Observable$` that emits when session should be renewed.
 *
 * We've added a special case for iframes when its made fullscreen, because we cannot know if a video inside it is playing,
 * that extends the session right away.
 */
const isIFrameElement = (element: Element): element is HTMLIFrameElement => element.tagName === 'IFRAME';
const extendImmediately$ = (
    action$: ActionsObservable<
        | InferAction<typeof Actions['extendSession']>
        | {type: typeof actionTypes.FETCH_ACCOUNT_SUCCESS}
        | {type: typeof actionTypes.FILE_UPLOAD_EXTEND_SESSION}
    >,
) =>
    merge(
        action$.pipe(
            ofType(actionTypes.FETCH_ACCOUNT_SUCCESS),
            // Only extend session on first account fetch
            take(1),
        ),
        action$.pipe(
            ofType(Actions.extendSession.type, actionTypes.FILE_UPLOAD_EXTEND_SESSION),
            map(identity), // Without this the inferred return type of `ofType` will become never ¯\_(ツ)_/¯
        ),
        fullScreenChange$.pipe(map(getFullScreenElement), filter(hasValue), filter(isIFrameElement)),
    );

/** ******************************
 * Epics
 ********************************/

/**
 * Epic responsible for calling the `stillAlive` endpoint.
 *
 * Entry-point epic that starts the entire flow. The extendSession$ observable returned at the end emits
 * the action `extendSessionSuccess`. This action triggers the other epics which "turns on" the auto-logout
 * loop of listening for user activity, counting down time until expiration, etc.
 */
export const extendSession: Epic<
    | InferAction<typeof Actions['extendSession']>
    | {type: typeof actionTypes.FETCH_ACCOUNT_SUCCESS}
    | {type: typeof actionTypes.FILE_UPLOAD_EXTEND_SESSION},
    any,
    RootState,
    {
        handleExtendSession: ExtendSessionObservable;
        persistExpirationTimestamp: PersistExpirationTimestamp;
    }
> = (action$, state$, {handleExtendSession, persistExpirationTimestamp}) =>
    extendImmediately$(action$).pipe(
        // Stop emits if auto logout is not enabled or if we are using wrong version
        filter(() => Selectors.canUseAutologout(state$.value)),
        // `exhaustMap` ensure we can only have _one_ request in-flight
        exhaustMap(() => extendSession$(handleExtendSession, persistExpirationTimestamp)),
    );

interface CustomActivity {
    type: 'custom-activity';
    description: string;
}
type CustomActivityObservable<T extends CustomActivity> = Observable<T>;
/**
 * Starts a timer to keep track of how long ago we last extended the session.
 *
 * When we detect activity from the user and the timer surpasses the threshold
 * we emit once to extend the session again.
 *
 * The customActivityObservable$ is originally implemented to keep track of whether Intercom
 * is open, and in that case keep emitting to keep the session alive. It's implemented this
 * way to keep this file free of external dependencies so we can keep Jest tests working.
 */
export const extendSessionTimer: Epic<
    InferAction<typeof Actions['extendSessionSuccess']>,
    any,
    RootState,
    {
        handleExtendSession: ExtendSessionObservable;
        customActivityObservable$?: CustomActivityObservable<CustomActivity>;
        persistExpirationTimestamp: PersistExpirationTimestamp;
        lastRefreshTimestamp$: LastRefreshTimestampObservable;
    }
> = (
    action$,
    state$,
    {handleExtendSession, customActivityObservable$, persistExpirationTimestamp, lastRefreshTimestamp$},
) => {
    /**
     * Combine userActivity$ with certain actions into an observable that shoul emit
     * a timestamp everytime something happens that we count as a session prolonging action
     */
    const formAndFileActions$ = action$.pipe(filter(action => hasValue(action.type.match(/redux-form|^FILE_/))));

    const lastActivityTimestamp$ = merge(userActivity$, formAndFileActions$, customActivityObservable$ || EMPTY).pipe(
        tap(activity => {
            if (checkIfFlagIsToggled(DebugLocalStorageFlags.SHOW_AUTO_LOGOUT_STATS.flag)) {
                debuggingSubjects.lastActivity$.next([debuggingActivityToString(activity), Date.now()]);
            }
        }),
        map(() => Date.now()),
    );

    return action$.pipe(
        ofType(Actions.extendSessionSuccess.type),
        // `switchMap` maintains an inner observable, that is discarded when triggered again.
        // This way the timer is "restarted" each time `extendSessionSuccess` emits.
        switchMap(() => {
            /**
             * Use combineLatest to construct observable that emits when:
             * - There's been at least one emit of user activity
             * - Session has been extended at least once
             * - At least 1000 ms has elapsed and then every 1000 ms afterwards
             */
            return combineLatest(
                lastActivityTimestamp$,
                lastRefreshTimestamp$.pipe(tap(timestamp => debuggingSubjects.lastRefresh$.next(timestamp))),
                timer(0, 1000),
            ).pipe(
                /**
                 * Allow emits to pass when the time since last refresh exceeds "defaultTimeBetweenExtend"
                 * i.e. 30 seconds
                 */
                filter(([, lastRefreshTimestamp]) => {
                    const timeSinceLastExtend = (Date.now() - lastRefreshTimestamp) / 1000;
                    return timeSinceLastExtend >= defaultTimeBetweenExtend;
                }),
                // Only take 1 emit
                take(1),
                // `exhaustMap` ensures we can only have 1 request in-flight
                exhaustMap(() => extendSession$(handleExtendSession, persistExpirationTimestamp)),
            );
        }),
    );
};

/**
 * Starts a timer that emits Redux actions when below a threshold.
 */
export const sessionExpirationTimer: Epic<
    InferAction<typeof Actions['extendSessionSuccess']>,
    any,
    RootState,
    {
        expirationTimestamp$: ExpirationTimestampObservable;
        /**
         * Tick describes how many times the internal timer has counted.
         * Used for unit testing.
         */
        getDeviceTimeDifference: (timestamp: UnixTimestamp, tick: number) => Seconds;
    }
> = (action$, state, {expirationTimestamp$, getDeviceTimeDifference}) => {
    // Capture latest UNIX timestamp
    return combineLatest(expirationTimestamp$, action$.pipe(ofType(Actions.extendSessionSuccess.type))).pipe(
        // `switchMap` to discard inner observable if we get a new emit
        switchMap(([timestamp]) => {
            // Tick every second
            return timer(0, 1000).pipe(
                /**
                 * To support unit testing we cannot directly reference device time like `Date.now`.
                 * Instead we ask the implementation to provide the time difference from the timestamp.
                 */
                map(tick => getDeviceTimeDifference(timestamp, tick)),
                // Only take emits until we reach 0
                takeWhile(secondsToExpiration => secondsToExpiration >= 0),
            );
        }),
        tap(timeLeft => {
            if (checkIfFlagIsToggled(DebugLocalStorageFlags.SHOW_AUTO_LOGOUT_STATS.flag)) {
                debuggingSubjects.timeToExpire$.next(timeLeft);
            }
        }),
        // Finally emit action to warn when close to session expiration
        map(timeLeft => {
            if (timeLeft <= defaultTimeLeftBeforeWarn) {
                return Actions.updateExpirationStatus.action({
                    status: 'close-to-expiration',
                    secondsToExpiration: timeLeft,
                });
            }
            return Actions.updateExpirationStatus.action({status: 'idle'});
        }),
        /**
         * distinctUntilChanged: "Only emit when the current value is different than the last"
         * We use this operator to only emit the action payload of status: 'idle' once.
         */
        distinctUntilChanged((a, b) => {
            if (a.payload.status === 'close-to-expiration' && b.payload.status === 'close-to-expiration') {
                return a.payload.secondsToExpiration === b.payload.secondsToExpiration;
            }
            return a.payload.status === b.payload.status;
        }),
    );
};

/**
 * When the expiration timer maintained in the `sessionExpirationTimer` epic reaches 0
 * we logout.
 */
export const triggerLogoutOnExpire: Epic<
    | InferAction<typeof Actions['updateExpirationStatus']>
    | InferAction<typeof Actions['extendSession']>
    | InferAction<typeof Actions['extendSessionSuccess']>,
    any,
    RootState
> = action$ =>
    action$.pipe(
        ofType(Actions.updateExpirationStatus.type),
        // Only take emits where secondsToExpiration is 0
        filter(
            ({payload}: InferAction<typeof Actions['updateExpirationStatus']>) =>
                payload.status === 'close-to-expiration' && payload.secondsToExpiration === 0,
        ),
        // Emit logout action
        map(() => Actions.logout.action()),
        // Only take 1 emits to make sure we don't call logout multiple times
        take(1),
        /**
         * Stop this observable if extendSession is emitted. This makes it possible to
         * request extendSession and even though the timer reaches 0 because of long request
         * time, logout will not be emitted. This should be safe because we subtract 60 seconds
         * from the actual expiration in extendSession$
         */
        takeUntil(action$.pipe(ofType(Actions.extendSession.type))),
        // `repeatWhen` "resets" this observable so we can emit again
        repeatWhen(() => action$.pipe(ofType(Actions.extendSessionSuccess.type))),
    );

/**
 * Handle logout side-effect on action
 */
export const logout: Epic<InferAction<typeof Actions['logout']>, any, RootState, {handleLogout: VoidFunction}> = (
    action$,
    state$,
    {handleLogout},
) =>
    action$.pipe(
        ofType(Actions.logout.type),
        tap(() => {
            handleLogout();
        }),
        ignoreElements(),
    );

/**
 * Debugging
 */
export const debuggingSubjects = {
    timeToExpire$: new Subject<Seconds>(),
    sessionLifetime$: new Subject<Seconds>(),
    lastActivity$: new Subject<[ReturnType<typeof debuggingActivityToString>, ReturnType<typeof Date.now>]>(),
    lastRefresh$: new ReplaySubject<ReturnType<typeof Date.now>>(),
};

const debuggingActivityToString = (
    activity: InferObservable<typeof userActivity$> | Action<string> | CustomActivity,
) => {
    if (activity instanceof Event) {
        return `User event: ${activity.type}`;
    } else if (activity instanceof HTMLVideoElement) {
        return 'Video playing in full screen';
    } else if (activity.type === 'custom-activity' && 'description' in activity) {
        return `Custom activity: ${activity.description}`;
    }
    return `Redux action: ${activity.type}`;
};
